import { sumMoney, serializeMoney } from '@erp/shared-utils';
import { SystemEntity } from '@erp/shared-types';
import type { EntityAssociationItem } from '@erp/rpc';
import { isProposedSplitLink } from './relatedLinesSummary';

export interface ConfirmedSplitPart {
  /** The charge that carries the part's amount. */
  chargeId: string;
  kind: 'LOAD' | 'PO' | 'PICKUP';
  /** What the part bills. */
  amount: string;
}

/**
 * The parts of an invoice line whose split AP accepted, one per allocation in
 * the order they were proposed. The links that mirror a part's money on other
 * files (companions) are left out: the part is already counted once. Empty when
 * the line has no accepted split.
 */
export function confirmedSplitParts(
  associations: readonly EntityAssociationItem[] | undefined,
): ConfirmedSplitPart[] {
  const parts: Array<ConfirmedSplitPart & { index: number }> = [];
  for (const item of associations ?? []) {
    const metadata = item.metadata;
    if (item.entity.entityType !== SystemEntity.CHARGE) continue;
    if (!metadata?.splitGroupId || isProposedSplitLink(item)) continue;
    if (metadata.splitRole !== 'primary') continue;
    if (!metadata.splitKind || !metadata.splitAllocatedAmount) continue;
    parts.push({
      chargeId: item.entity.entityId,
      kind: metadata.splitKind,
      amount: metadata.splitAllocatedAmount,
      index: metadata.splitPartIndex ?? 0,
    });
  }
  return parts
    .sort((a, b) => a.index - b.index)
    .map(({ chargeId, kind, amount }) => ({ chargeId, kind, amount }));
}

/** What the accepted parts add up to. */
export function confirmedSplitTotal(
  parts: readonly ConfirmedSplitPart[],
): string {
  return serializeMoney(sumMoney(parts.map((part) => part.amount)));
}
